"use client";

import type { RootState } from "@/store/store";
import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { LEAD_DROP_FORM_VARIANTS } from "../../../constants";

/**
 * Client-only twins of `LEAD_DROP_FORM_SECTION_VARIANTS`. The shared registry
 * declares these as `ssr: true`; here they must be `ssr: false` because nothing
 * in this file ever runs on the server.
 */
const FORM_VARIANTS: Record<
	string,
	// Each variant declares its own `data` shape; this registry is intentionally loose.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ComponentType<any>
> = {
	[LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_1]: dynamic(
		() => import("@/widgets/leaddropform/LeaddropForm1"),
		{ ssr: false },
	),
	[LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2]: dynamic(
		() => import("@/widgets/leaddropform/LeaddropForm2"),
		{ ssr: false },
	),
	[LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_10]: dynamic(
		() => import("@/widgets/leaddropform/LeaddropForm10"),
		{ ssr: false },
	),
	[LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6]: dynamic(
		() => import("@/widgets/leaddropform/MultiStepIntakeForm"),
		{ ssr: false },
	),
	// The CMS-authored custom form. Present here as well as in the shared registry because
	// this map is the client-only twin — a variant missing from it renders nothing at all.
	[LEAD_DROP_FORM_VARIANTS.CUSTOM_FORM]: dynamic(
		() => import("@/components/organisms/leadForm/LeadFormSurface"),
		{ ssr: false },
	),
	[LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_2001]: dynamic(
		() => import("@/widgets/leaddropform/LeaddropForm2001"),
		{ ssr: false },
	),
};

/**
 * The admin-configured inline lead form, resolved at runtime.
 *
 * Exists so that a *host widget* never has to branch on Redux to decide whether
 * to render a form. Which variant to use lives in `state.leaddropform.variant`,
 * which is written by ClientWrapper's mount effect and so is always null while
 * the server renders. A host that used it to choose whether to render a
 * `dynamic()` child changed the shape of the tree between the server and the
 * first client render: `ssr: false` wraps its child in a Suspense boundary
 * (`hasSuspenseBoundary = !opts.ssr || !!opts.loading` in next/dynamic's
 * loadable.js) and the server writes real boundary markers into the HTML for it,
 * so a boundary that exists only on the client is a hydration mismatch — even
 * though it renders nothing. Hosts therefore mount THIS component
 * unconditionally (gated only on their own CMS flag, the way Banner9 does), and
 * the store read happens in here, past hydration, where there is nothing left to
 * compare against.
 *
 * Renders nothing until its chunk resolves and a variant is configured, so the
 * host should reserve the space it needs (e.g. an `:empty` width rule).
 */
export default function InlineLeadForm() {
	const variant = useSelector((state: RootState) => state.leaddropform.variant);
	const formRow = useSelector((state: RootState) => {
		const blob = state.leaddropform.lead_drop_form as
			| { formData?: Record<string, unknown> }
			| null
			| undefined;
		return blob?.formData ?? null;
	});

	const Form = useMemo(() => {
		if (!variant) return null;
		return FORM_VARIANTS[variant] ?? null;
	}, [variant]);

	if (!Form) return null;

	// Form 6 (multi-step intake) is the only variant driven by the CMS row rather
	// than reading the store itself.
	if (variant === LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 && formRow) {
		return <Form data={{ data: formRow }} />;
	}
	return <Form />;
}
